import React from 'react';
import CardView from '../../../components/Redesing/card-view';
import { PropertyList } from '../../../components/Redesing/property-list';
import { PropertyListItem } from '../../../components/Redesing/property-list-item';
import { SeverityPill } from '../../../components/Redesing/severity-pill';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { getLocaleDateTimeString, timeForHumans, toTitleCase } from '../../../utils';
import RentalImageGallery from '../../../components/RentalImageGallery/RentalImageGallery';

interface ErpAssessment {
  tag: string;
  note?: string;
}

interface EarlyEndReport {
  tags: string[];
  description?: string | null;
  duration_at_report?: number;
  reported_on?: string;
  photo_urls?: string[];
}

interface EarlyEndReview {
  action?: string;
  is_safety_issue?: boolean;
  reviewed_at?: string;
  reviewer_slack_id?: string;
}

interface RentalEndDataProps {
  data: {
    erp_assessment?: ErpAssessment | null;
    early_end_report?: EarlyEndReport | null;
    early_end_review?: EarlyEndReview | null;
  };
}

const ERP_TAG_COLORS: Record<string, 'success' | 'warning' | 'error'> = {
  safe_to_go: 'success',
  cosmetic_damage: 'warning',
  unsafe_for_next_user: 'error',
};

const RentalEndData = ({ data }: RentalEndDataProps) => {
  const { erp_assessment, early_end_report, early_end_review } = data;
  const hasData = erp_assessment || early_end_report || early_end_review;

  if (!hasData) return null;

  const earlyEndPhotos = (early_end_report?.photo_urls || []).map((link) => ({
    link,
    category: 'Early End Photo',
  }));

  return (
    <>
      {erp_assessment && (
        <CardView
          title="End Rental Assessment"
          content={
            <PropertyList>
              <PropertyListItem divider label="Tag">
                <SeverityPill color={ERP_TAG_COLORS[erp_assessment.tag] || 'info'}>
                  {toTitleCase(erp_assessment.tag.replace(/_/g, ' '))}
                </SeverityPill>
              </PropertyListItem>
              {erp_assessment.note && (
                <PropertyListItem divider={false} label="Note">
                  <Typography color="text.secondary" variant="subtitle1">
                    {erp_assessment.note}
                  </Typography>
                </PropertyListItem>
              )}
            </PropertyList>
          }
        />
      )}

      {early_end_report && (
        <CardView
          title="Early End Report"
          content={
            <>
              <PropertyList>
                {early_end_report.tags?.length > 0 && (
                  <PropertyListItem divider label="Tags">
                    <Typography color="text.secondary" variant="subtitle1">
                      {early_end_report.tags
                        .map((t) => toTitleCase(t.replace(/_/g, ' ')))
                        .join(', ')}
                    </Typography>
                  </PropertyListItem>
                )}
                {early_end_report.description && (
                  <PropertyListItem divider label="Description">
                    <Typography color="text.secondary" variant="subtitle1">
                      {early_end_report.description}
                    </Typography>
                  </PropertyListItem>
                )}
                {early_end_report.duration_at_report != null && (
                  <PropertyListItem divider label="Duration at report">
                    <Typography color="text.secondary" variant="subtitle1">
                      {timeForHumans(early_end_report.duration_at_report)}
                    </Typography>
                  </PropertyListItem>
                )}
                {early_end_report.reported_on && (
                  <PropertyListItem divider={false} label="Reported on">
                    <Typography color="text.secondary" variant="subtitle1">
                      {getLocaleDateTimeString(early_end_report.reported_on)}
                    </Typography>
                  </PropertyListItem>
                )}
              </PropertyList>
              {earlyEndPhotos.length > 0 && (
                <>
                  <Divider />
                  <RentalImageGallery images={earlyEndPhotos} title="Early End Photos" />
                </>
              )}
            </>
          }
        />
      )}

      {early_end_review && (
        <CardView
          title="Early End Review"
          content={
            <PropertyList>
              {early_end_review.action && (
                <PropertyListItem divider label="Action">
                  <Typography color="text.secondary" variant="subtitle1">
                    {toTitleCase(early_end_review.action.replace(/_/g, ' '))}
                  </Typography>
                </PropertyListItem>
              )}
              {early_end_review.is_safety_issue != null && (
                <PropertyListItem divider label="Safety issue">
                  <SeverityPill
                    color={early_end_review.is_safety_issue ? 'error' : 'success'}
                  >
                    {early_end_review.is_safety_issue ? 'Yes' : 'No'}
                  </SeverityPill>
                </PropertyListItem>
              )}
              {early_end_review.reviewed_at && (
                <PropertyListItem divider label="Reviewed at">
                  <Typography color="text.secondary" variant="subtitle1">
                    {getLocaleDateTimeString(early_end_review.reviewed_at)}
                  </Typography>
                </PropertyListItem>
              )}
              {early_end_review.reviewer_slack_id && (
                <PropertyListItem divider={false} label="Reviewer (Slack)">
                  <Typography color="text.secondary" variant="subtitle1">
                    {early_end_review.reviewer_slack_id}
                  </Typography>
                </PropertyListItem>
              )}
            </PropertyList>
          }
        />
      )}
    </>
  );
};

export default RentalEndData;
